import { Switch, Text } from '@byecode/ui'
import type { ChartValue, FilterBlockAbstract } from '@lighthouse/core'
import { type DataSourceAbstract, FilterMode } from '@lighthouse/core'
import {
    filterBlock,
    getAppointField,
    getIsFindUseInFilter,
    getViewColumns,
    ListItem4ByecodeUi,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    ViewFieldFilterConfigure
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useUpdateEffect } from 'react-use'

import { pageSyncComponentsAtom } from '@/atoms/application/state'
import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pageBlocksAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { useBlockSettingContext } from '@/contexts/BlockSettingContext'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { FilterControl } from '../../Common/FilterControl'
import { ListItemMenu } from '../../Common/ListItemMenu'
import { addPrefixNameToFormName } from '../utils'

interface SwitchList {
    label: string
    value: string
}
interface BaseDataConfiguratorProps {
    prefixName?: string
    dataSource: DataSourceAbstract
    dataSourceList: DataSourceAbstract[]
    switchList?: SwitchList[]
}

export const BaseDataConfigurator: React.FC<BaseDataConfiguratorProps> = ({ prefixName, dataSource, dataSourceList, switchList = [] }) => {
    const { scope } = useBlockSettingContext()
    const { control, watch, getValues, setValue } = useFormContext()
    const aggregate = watch('aggregate')
    const [_, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )
    const disabledWithVersion = useIsDisabledWithVersion()
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    // name="mainAxis"
    const findUseObject = useFindUseObjectContext()
    const ruleFilterFormName = addPrefixNameToFormName('ruleFilter', prefixName)
    const ruleFilter = watch(ruleFilterFormName)
    const pageBlocks = useAtomData(pageBlocksAtom(pageId))
    const syncComponents = useAtomData(pageSyncComponentsAtom)
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const {
        id: dsId,
        schema,
        viewOptions: { tableProps }
    } = dataSource

    const columns = getViewColumns({
        tableProps,
        schema
    })

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const isHighLight = useMemo(
        () =>
            getIsFindUseInFilter({
                findUseObject,
                currentDsId: dsId,
                data: ruleFilter
            }),
        [dsId, findUseObject, ruleFilter]
    )

    const primaryField = useMemo(() => {
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    // 筛选器blocks
    const filterBlocks = useMemo(() => {
        if (!pageBlocks) {
            return []
        }
        if (scope) {
            return filterBlock(syncComponents, block => block.type === 'filter') as FilterBlockAbstract[]
        }

        return filterBlock(pageBlocks, block => block.type === 'filter') as FilterBlockAbstract[]
    }, [pageBlocks, scope, syncComponents])

    useUpdateEffect(() => {
        if (!aggregate) {
            const mainAxis: ChartValue[] | undefined = getValues('mainAxis')
            const secondaryAxis: ChartValue[] | undefined = getValues('secondaryAxis')
            if (mainAxis) {
                const newMainAxis = mainAxis.filter(item => schema?.[item.fieldId].innerType === 'NUMBER')
                setValue('mainAxis', newMainAxis)
            }
            if (secondaryAxis) {
                const newSecondaryAxis = secondaryAxis.filter(item => schema?.[item.fieldId].innerType === 'NUMBER')
                setValue('secondaryAxis', newSecondaryAxis)
            }
        }
    }, [aggregate])

    return (
        <>
            {switchList.map(({ value, label }) => {
                return (
                    <ListItem4ByecodeUi key={value} justifyContent="space-between" alignItems="center">
                        <Text>{label}</Text>
                        <Controller
                            control={control}
                            name={addPrefixNameToFormName(value, prefixName)}
                            render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                        />
                    </ListItem4ByecodeUi>
                )
            })}
            <Controller
                name={ruleFilterFormName}
                control={control}
                render={({ field: { onChange, value } }) => {
                    const filter = value
                    const filtersLength = filter?.expression?.conditions?.length || 0
                    return (
                        <ListItemMenu
                            width="auto"
                            highlighting={isHighLight}
                            label="筛选数据"
                            positionDependencies={[filter]}
                            value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                            popupContent={
                                <ViewFieldFilterConfigure
                                    dataSource={dataSource}
                                    dataSourceList={dataSourceList}
                                    pagePathParameter={pagePathParameter}
                                    pageQueryParameter={pageQueryParameter}
                                    pageVariables={pageVariables}
                                    pageStateParameter={pageStateParameter}
                                    disabled={disabledWithVersion}
                                    availableFieldBlocksInfo={availableFieldBlocksInfo}
                                    availableFilterBlocksInfo={availableFilterBlocksInfo}
                                    fieldMode={FilterMode.CUSTOM}
                                    paramsMode={FilterMode.CUSTOM}
                                    columns={[]}
                                    primaryField={primaryField}
                                    filter={filter}
                                    onFilter={onChange}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                />
                            }
                        />
                    )
                }}
            />
            <FilterControl
                dataSource={dataSource}
                columns={columns}
                enableHover
                filterBlocks={filterBlocks}
                disableIcon
                // value={field.value}
                // onChange={field.onChange}
            />
        </>
    )
}
